/**
 * customers-api.ts — typed feature wrapper + wire→view-model mapping for the
 * T1 Kunden list (Story V10-J).
 *
 * The live wire shape (CustomerListItemDto, GET /customers) mismatches the mock
 * view-model (KundeRow) — per V10-J AC3 the mapping lives HERE in the client
 * layer, not in the components.
 *
 * Mapping decisions (display-only; no domain rules recomputed client-side):
 *   - status: carried VERBATIM. Story E1 deleted the old 6→3 collapse (and with
 *     it the invented 'prospect'): a status dropdown cannot sit on a lossy map,
 *     and the api is the authority on the lifecycle set (rule #2).
 *   - subjectType: needed by the E1 status write route (/:subjectType/:id).
 *   - statusSince: E4, carried as-is (null stays null, never created_at).
 *   - allowedTransitions: E1, the api's legal targets for this row, verbatim.
 *   - ort / kanton: firma.city / firma.canton, the detail-header subtitle "Ort · Kanton" (G1-1).
 *   - branche: not on the wire yet → city (display fallback; carry-forward). NOT used in the
 *     header subtitle anymore (that was the "Ort · Ort" bug — city was rendered as both segments).
 *   - risiko/betreuer/kontakt: not on the wire yet → defaults. kontakt is NO LONGER fed from
 *     customer_number (F5): the number now has its own `kundennummer` field.
 *   - kundennummer: dto.customer_number verbatim (F5, always present, K-nnnnnn).
 *   - jahrespraemie: premium_sum numeric-string → number (0 when null/denied).
 *   - mailTeamId / mailTeamName: MV, the client's mail-visibility team, verbatim.
 *   - isUnconfirmed: dto.is_unconfirmed verbatim (false when the wire omits it). An intersection over
 *     the base KundeRow rather than a second row type, so the T1 grid reads it without
 *     redeclaring the wire DTO.
 *
 * [Source: apps/web/src/features/communication/api/tasks-api.ts] (wrapper pattern)
 * [Source: docs/stories/v10-j-web-wiring-core.md AC1/AC3]
 */

import { apiClient } from '../../../lib/api-client';
import type { CustomerListParams } from '../../../lib/api-client';
import type { CustomerListItemDto } from '@brokly/shared';
import type { KundeRow as BaseKundeRow } from '../../entity-detail/types';

// Re-export for convenience
export type { CustomerListParams };

/** The T1 grid's KundeRow, plus the unconfirmed flag (see the header note). */
export type KundeRow = BaseKundeRow & { isUnconfirmed?: boolean };

/** One wire row → the KundeRow view-model the T1 grid renders. */
export function mapCustomerToKundeRow(dto: CustomerListItemDto): KundeRow {
  return {
    id: dto.subject_id,
    subjectType: dto.subject_type,
    name: dto.name ?? '-',
    branche: dto.city ?? '',
    status: dto.status,
    betreuer: '',
    kontakt: '',
    kundennummer: dto.customer_number,
    ort: dto.city ?? '',
    kanton: dto.canton ?? '',
    statusSince: dto.status_since,
    inactivatedAt: dto.inactivated_at ?? null,
    allowedTransitions: dto.allowed_transitions,
    policen: dto.policy_count,
    jahrespraemie: dto.premium_sum !== null ? Number(dto.premium_sum) : 0,
    mailTeamId: dto.mail_team_id,
    mailTeamName: dto.mail_team_name,
    isUnconfirmed: dto.is_unconfirmed ?? false,
  };
}

/**
 * Fetch + map one page of the Kunden-grid rows (GET /customers).
 * UI-U3: returns the keyset `nextCursor` alongside the items so the list can
 * offer "Mehr laden" (page.next_cursor may be absent on older wires → null).
 */
export async function fetchKundeRows(
  params: CustomerListParams = {},
): Promise<{ items: KundeRow[]; nextCursor: string | null }> {
  const response = await apiClient.customers.list(params);
  return {
    items: response.items.map(mapCustomerToKundeRow),
    nextCursor: response.page?.next_cursor ?? null,
  };
}
